import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";

import { ChartLegend } from "@/components/Charts/ChartLegend";
import { dataColors } from "@/components/Charts/theme";
import { parseChartDate } from "@/components/Charts/util";
import { NoData } from "@/components/NoData/NoData";
import { Stack } from "@/design-system/Stack";
import { BarChart } from "@/modules/analytics/components/BarChart";
import { type DateRangeOption, filterDataByDateRange } from "@/modules/analytics/util";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";
import { RuntimeColorDotLabel } from "@/ui-adapters/RuntimeColorBadge";

import { AnalyticsCard } from "../../../components/AnalyticsCard";

interface SolvedMessagesCardProps {
  dateRangeOptions: NonEmptyArray<DateRangeOption>;
}
const RANGE = "lastYear";

export function QuestionsAskedAndSolvedCard({ dateRangeOptions }: SolvedMessagesCardProps): React.ReactNode {
  const [dateRange, setDateRange] = useState<DateRangeOption>(dateRangeOptions[0]);

  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const { data } = useSuspenseQuery(analyticsQueries.askForHelpStats(RANGE));

  const trimmedData = filterDataByDateRange(data.data, dateRange);
  const primaryData = data
    ? trimmedData.map((dataPoint) => ({
        x: parseChartDate(dataPoint.range).toISOString(),
        y: dataPoint.askHelpCreatedCount,
        isPending: dataPoint.isCurrentMonth,
      }))
    : [];
  const secondaryData = data
    ? trimmedData.map((dataPoint) => ({
        x: parseChartDate(dataPoint.range).toISOString(),
        y: dataPoint.askHelpResolvedCount,
        isPending: dataPoint.isCurrentMonth,
      }))
    : [];
  const noData = data.data.length === 0;

  return (
    <AnalyticsCard.Root variant="graph">
      <AnalyticsCard.Header
        tooltip={t("page.analytics-overview.chart.solved-messages.title-tooltip")}
        title={t("page.analytics-overview.chart.solved-messages.title")}
      >
        <AnalyticsCard.FilterSelect
          items={dateRangeOptions}
          selected={dateRange}
          onChange={setDateRange}
          renderOption={(x) => x.name}
          keySelector={(x) => x.value}
        />
      </AnalyticsCard.Header>
      {noData ? (
        <NoData />
      ) : (
        <figure className="flex flex-1 flex-col items-center gap-space-md">
          <BarChart
            yTickMode="integer"
            dataPrimary={{ data: primaryData }}
            dataSecondary={{ data: secondaryData }}
            xAxisType="date"
            yLabel={t("page.analytics-overview.chart.solved-messages.y-axis-label")}
            hoverLabel={(bar) => {
              const isPending = bar.primary?.isPending || bar.secondary?.isPending;

              return (
                <Stack>
                  <RuntimeColorDotLabel color={dataColors.primary}>
                    <span className="flex flex-1 justify-between gap-space-sm">
                      {t("page.analytics-overview.chart.solved-messages.data-point.tooltip.primary", {
                        amount: bar.primary?.y || 0,
                      })}
                    </span>
                  </RuntimeColorDotLabel>
                  <RuntimeColorDotLabel color={dataColors.green}>
                    <span className="flex flex-1 justify-between gap-space-sm">
                      {t("page.analytics-overview.chart.solved-messages.data-point.tooltip.secondary", {
                        amount: bar.secondary?.y || 0,
                      })}
                    </span>
                  </RuntimeColorDotLabel>
                  {isPending && (
                    <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                  )}
                </Stack>
              );
            }}
          />

          <ChartLegend
            items={[
              {
                label: t("page.analytics-overview.chart.solved-messages.legend.help-questions-asked"),
                color: dataColors.primary,
              },
              {
                label: t("page.analytics-overview.chart.solved-messages.legend.help-questions-solved"),
                color: dataColors.green,
              },
              {
                label: t("page.analytics-overview.chart.solved-messages.legend.help-questions-asked-current-period"),
                color: dataColors.primary,
                pattern: "striped",
              },
              {
                label: t("page.analytics-overview.chart.solved-messages.legend.help-questions-solved-current-period"),
                color: dataColors.green,
                pattern: "striped",
              },
            ]}
          />
        </figure>
      )}
    </AnalyticsCard.Root>
  );
}
